/**
 * Tela exibida apenas nas apex centrais (escolarai.com.br / front-v2.escolarai.com.br)
 * quando ainda não há base escolhida. Renderiza FORA do ApolloProvider/contexts:
 * por isso só usa componentes puros (sem GraphQL, sem auth).
 *
 * Fonte das bases: GET /api/tenants/active (com fallback para @/config/bases).
 * Ao escolher: persiste o slug e redireciona para a base (loginUrl do backend,
 * ou URL montada a partir do slug quando vier do fallback).
 */
import React, { useEffect, useMemo, useState } from "react";
import { BASES } from "@/config/bases";
import {
  fetchActiveTenants,
  type PublicTenantInfo,
} from "@/services/tenants/publicTenants";
import type { CentralEnv } from "@/lib/tenant-host";
import { buildBaseUrl, saveBaseSlug } from "@/lib/tenant-host";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface CentralBaseSelectorProps {
  env: CentralEnv;
}

/** Modelo de exibição unificado (API viva ou fallback). loginUrl é opcional. */
interface TenantOption {
  slug: string;
  name: string;
  shortName?: string;
  logoUrl?: string;
  primaryColor?: string;
  loginUrl?: string;
}

const FALLBACK_OPTIONS: TenantOption[] = BASES;

function toOptions(tenants: PublicTenantInfo[]): TenantOption[] {
  return tenants.map((t) => ({
    slug: t.slug,
    name: t.name,
    shortName: t.shortName,
    logoUrl: t.logoUrl,
    primaryColor: t.primaryColor,
    loginUrl: t.loginUrl,
  }));
}

export function CentralBaseSelector({ env }: CentralBaseSelectorProps) {
  const [opcoes, setOpcoes] = useState<TenantOption[]>(FALLBACK_OPTIONS);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState("");
  const [selecionado, setSelecionado] = useState<string | null>(null);

  // Fonte primária: endpoint público. Em erro, mantém o fallback de config.
  useEffect(() => {
    const ctrl = new AbortController();
    fetchActiveTenants(ctrl.signal)
      .then((tenants) => {
        if (tenants.length > 0) setOpcoes(toOptions(tenants));
      })
      .catch(() => {
        /* mantém FALLBACK_OPTIONS */
      })
      .finally(() => setCarregando(false));
    return () => ctrl.abort();
  }, []);

  const bases = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return opcoes;
    return opcoes.filter(
      (b) =>
        b.name.toLowerCase().includes(q) ||
        b.slug.toLowerCase().includes(q) ||
        b.shortName?.toLowerCase().includes(q),
    );
  }, [busca, opcoes]);

  const entrar = (opt: TenantOption) => {
    saveBaseSlug(opt.slug);
    // Prioriza a URL pronta do backend; cai para a URL sensível ao ambiente.
    const destino = opt.loginUrl ?? buildBaseUrl(opt.slug, env, "/login");
    window.location.replace(destino);
  };

  const selecionadoOpt = bases.find((b) => b.slug === selecionado) ?? null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md rounded-xl border bg-card text-card-foreground shadow-sm p-6 sm:p-8">
        <div className="flex flex-col items-center text-center mb-6">
          <img src="/logo.png" alt="EscolarAI" className="h-12 mb-4" />
          <h1 className="text-xl font-semibold">Selecione sua escola</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Escolha a rede/escola para acessar o sistema.
          </p>
          {env === "homol" && (
            <span className="mt-2 inline-flex items-center rounded-full bg-amber-100 text-amber-800 text-xs px-2 py-0.5">
              Ambiente de homologação
            </span>
          )}
        </div>

        <Input
          autoFocus
          placeholder="Buscar escola..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          className="mb-3"
        />

        <div className="max-h-[55vh] sm:max-h-72 overflow-y-auto overscroll-contain -mx-1 px-1 space-y-1">
          {carregando && bases.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">
              Carregando escolas…
            </p>
          )}
          {!carregando && bases.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-6">
              Nenhuma escola encontrada.
            </p>
          )}
          {bases.map((b) => {
            const ativo = selecionado === b.slug;
            return (
              <button
                key={b.slug}
                type="button"
                onClick={() => setSelecionado(b.slug)}
                onDoubleClick={() => entrar(b)}
                className={cn(
                  "w-full flex items-center gap-3 text-left rounded-lg border px-3 py-2.5 text-sm transition-colors min-h-[44px]",
                  "hover:bg-accent hover:text-accent-foreground",
                  ativo
                    ? "border-primary ring-1 ring-primary bg-accent"
                    : "border-transparent",
                )}
                style={
                  ativo && b.primaryColor
                    ? { borderColor: b.primaryColor }
                    : undefined
                }
              >
                {b.logoUrl ? (
                  <img
                    src={b.logoUrl}
                    alt=""
                    className="h-8 w-8 rounded object-contain shrink-0"
                    onError={(e) => {
                      e.currentTarget.style.display = "none";
                    }}
                  />
                ) : (
                  <span
                    className="h-8 w-8 rounded shrink-0 flex items-center justify-center text-xs font-semibold text-white"
                    style={{ backgroundColor: b.primaryColor ?? "#64748b" }}
                  >
                    {(b.shortName ?? b.name).charAt(0).toUpperCase()}
                  </span>
                )}
                <span className="min-w-0">
                  <span className="font-medium block truncate">
                    {b.shortName ?? b.name}
                  </span>
                  {b.shortName && b.shortName !== b.name && (
                    <span className="block text-xs text-muted-foreground truncate">
                      {b.name}
                    </span>
                  )}
                </span>
              </button>
            );
          })}
        </div>

        <Button
          className="w-full mt-5 min-h-[44px]"
          disabled={!selecionadoOpt}
          onClick={() => selecionadoOpt && entrar(selecionadoOpt)}
        >
          Entrar
        </Button>
      </div>
    </div>
  );
}

/** Splash mínimo mostrado enquanto o redirect para a base acontece. */
export function CentralRedirectSplash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30">
      <p className="text-sm text-muted-foreground animate-pulse">
        Redirecionando…
      </p>
    </div>
  );
}
